import React from 'react';

import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import Table from '../../../../Table';
import AddButton from '../../../../AddButton';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';

import { useServicerStore } from '../../../../../store';
import LoanService from '../../../../../services/LoanService';
import formatCurrency from '../../../../../helpers/formatCurrency';

const tabs = [
  {
    label: 'Payments',
  },
  {
    label: 'Demands',
  },
];

export default function Payments({ loanId }) {
  const navigate = useNavigate();

  const userRole = useServicerStore((state) => state.userRole);
  const isSuperAdmin = userRole === 'superAdmin';

  const { data: demands, isFetching } = useQuery({
    queryKey: ['demands', loanId],
    queryFn: () => LoanService.getLoanDemands(loanId),
    enabled: !!loanId,
  });

  const cols = [
    {
      title: 'Date Effective',
      keys: ['date_effective'],
      format: (value) => moment(value).format('M/D/YY'),
      link: (item) => {
        return `/loans/${loanId}/demands/${item.id}?tab=Demand`;
      },
    },
    {
      title: 'Status',
      keys: ['status'],
      link: (item) => {
        return `/loans/${loanId}/demands/${item.id}?tab=Demand`;
      },
    },
    {
      title: 'Amount',
      keys: ['amount'],
      type: 'currency',
    },
    {
      title: 'Invoices',
      keys: ['invoice_id'],
      format: (value) => (value ? value.length : 0),
    },
    {
      title: 'Payments',
      keys: ['payment_id'],
      type: 'list',
      createListItem: (item) => {
        return `${
          item._payment?.date ? moment(item._payment.date).format('M/D/YY') : ''
        } ${
          item._payment?.amount
            ? `$${formatCurrency(item._payment.amount)}`
            : '$0.00'
        }`;
      },
      createOnClick: (item, row) => {
        return () => {
          navigate(
            `/loans/${row?.loan_id}/payments/${item._payment?.id}?tab=Payment`
          );
        };
      },
    },
    {
      title: 'Delivery',
      keys: ['delivery'],
      format: (value) => {
        const latestDelivery = value?.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        )[0];
        return latestDelivery?._mail?.lob_status || '';
      },
      link: (row) => `/loans/${loanId}/demands/${row.id}?tab=Demand`,
    },
    {
      title: 'Acceleration date',
      keys: ['acceleration_date'],
      type: 'date',
      tooltip: (row) => {
        return row.acceleration_attorney_reviewed
          ? ''
          : 'This acceleration has not been reviewed by the attorney.';
      },
      tooltipSvgClassName: '!text-red-500',
      fixedTooltip: true,
      tooltipSize: 'small',
    },
    {
      title: 'Sale date',
      keys: ['sale_date'],
      type: 'date',
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37.13px]">
        <AddOrEditPageTabs tabs={tabs} />
        {isSuperAdmin && (
          <AddButton onClick={() => navigate(`/loans/${loanId}?tab=Demand`)}>
            Create Demand
          </AddButton>
        )}
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6 flex justify-between w-full">
          Demands
        </h2>

        <Table
          cols={cols}
          rows={demands}
          title="Demands"
          isFetching={isFetching}
          minHeight={565}
        />
      </div>
    </div>
  );
}
